import { toFinalScorePercent } from '@breakoutlearning/firebase-repository/sources-of-truth/percentage'
import { defaultScoreMappingForRubricType } from '@breakoutlearning/firebase-repository/sources-of-truth/assignmentRubricDefaults'
import { RubricTypeList } from '@breakoutlearning/firebase-repository/types'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { AppealOverrideIndicator } from 'components/AppealOverrideIndicator'
import { GoogleStorageImage } from 'components/GoogleStorageImage'
import { InfoIcon } from 'components/icons/Info'
import { SparklesIcon } from 'components/icons/Sparkles'
import { SlideDeckDescriptionMarkdown } from 'components/SlideDeckDescriptionMarkdown'
import { useCurrentUser } from 'hooks/auth'
import type { useMeetingResultsCubit } from 'hooks/cubits/meetingsResults'
import { useFeatureFlags } from 'hooks/featureFlags'
import { useRubricTypes } from 'hooks/useRubricTypes'
import { useTranslationTyped } from 'i18n/i18n'
import { ArrowLeft, BookOpen, Check, ChevronDown, X } from 'lucide-react'
import { DateTime } from 'luxon'
import { observer } from 'mobx-react-lite'
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import {
  useScoreComponents,
  type ScoreComponent,
  type ScoreComponentKey,
} from './scoreComponents'
import { useGradeExplainer } from './useGradeExplainer'
import { useGradeAppeal } from './useGradeAppeal'
import { GradeAppealActionButton } from './GradeAppealActionButton'
import { GradeAppealForm } from './GradeAppealFormDialog'
import { GradeAppealStatusPanel } from './GradeAppealStatusPanel'

type GradeDetails = NonNullable<ReturnType<typeof useGradeExplainer>['details']>
type GradeComponentName = keyof GradeDetails['components']
type ComponentBreakdownRow = {
  key: GradeComponentName
  label: string
  componentKey: ScoreComponentKey
  score: number | null
  weight: number | null
}

function percent(score: number | null): string {
  return score === null ? '—' : `${Math.round(score * 100)}%`
}

function duration(seconds: number): string {
  return `${Math.round(seconds / 60)} min`
}

function EvidenceUnavailable() {
  const { tt } = useTranslationTyped()
  return (
    <p className="text-body-small text-on-surface-var">
      {tt.meeting.results.grade_explainer.evidence_unavailable()}
    </p>
  )
}

function WeightedImpact({
  score,
  weight,
  lowered,
}: {
  score: number | null
  weight: number | null
  lowered: boolean
}) {
  const { tt } = useTranslationTyped()
  const copy = tt.meeting.results.grade_explainer
  if (weight === null) {
    return <NeutralImpact />
  }
  if (lowered) {
    return (
      <LossImpact
        percentage={
          score === null
            ? null
            : weightedPercentage({ score, weight, lowered: true })
        }
      />
    )
  }
  return (
    <span className="shrink-0 text-xs font-bold text-bi-deep-green tabular-nums">
      {score === null
        ? '+—%'
        : copy.percentage_earned({
            percentage: weightedPercentage({ score, weight, lowered: false }),
          })}
    </span>
  )
}

function NeutralImpact({ label }: { label?: string }) {
  return (
    <span
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className="inline-block h-0.5 w-4 shrink-0 rounded-full bg-bi-on-surface-disabled"
    />
  )
}

function LossImpact({
  percentage,
  size = 'small',
}: {
  percentage: number | null
  size?: 'small' | 'regular'
}) {
  const { tt } = useTranslationTyped()
  const copy = tt.meeting.results.grade_explainer
  if (percentage === 0) {
    return <NeutralImpact label={copy.no_score_lost()} />
  }
  return (
    <span
      className={`shrink-0 font-bold text-bi-error tabular-nums ${size === 'regular' ? 'text-sm' : 'text-xs'}`}
    >
      {percentage === null ? '−—%' : copy.percentage_lowered({ percentage })}
    </span>
  )
}

function allocatePercentages({
  total,
  shares,
}: {
  total: number
  shares: Array<number>
}): Array<number> {
  const totalCents = Math.round(total * 100)
  const shareSum = shares.reduce((sum, share) => sum + share, 0)
  if (shareSum === 0) return shares.map(() => 0)
  const exact = shares.map((share) => (totalCents * share) / shareSum)
  const cents = exact.map(Math.floor)
  const remainder = totalCents - cents.reduce((sum, value) => sum + value, 0)
  const order = exact
    .map((value, index) => ({ index, fraction: value - cents[index] }))
    .sort((left, right) => right.fraction - left.fraction)
  for (let index = 0; index < remainder; index++) cents[order[index].index]++
  return cents.map((value) => value / 100)
}

function rubricLossShares({
  contribution,
  studentRole,
}: {
  contribution: GradeDetails['components']['contribution']
  studentRole: string | null
}): Array<number> | null {
  if (contribution.evidence.status !== 'available') return null
  const rubricLosses = contribution.evidence.rubrics.map((rubric) => {
    const rubricType = RubricTypeList.find((type) => type === rubric.rubricType)
    const mapping =
      rubric.scoreMapping ??
      (rubricType === undefined
        ? undefined
        : defaultScoreMappingForRubricType(rubricType))
    const mappedScore = mapping?.[String(rubric.score)]
    const rubricWeight =
      rubric.weights === null
        ? 1
        : 'allRoles' in rubric.weights
          ? rubric.weights.allRoles
          : studentRole === null
            ? null
            : (rubric.weights.byRole[studentRole] ?? null)
    if (mappedScore === undefined || rubricWeight === null) return null
    return { score: mappedScore / 100, weight: rubricWeight }
  })
  if (rubricLosses.some((rubric) => rubric === null)) return null
  const totalWeight = rubricLosses.reduce(
    (sum, rubric) => sum + (rubric?.weight ?? 0),
    0
  )
  if (totalWeight === 0) return null
  return rubricLosses.map(
    (rubric) =>
      ((1 - (rubric?.score ?? 1)) * (rubric?.weight ?? 0)) / totalWeight
  )
}

function EvidenceSection({
  visible,
  children,
}: {
  visible: boolean
  children: ReactNode
}) {
  return visible ? <section>{children}</section> : null
}

export function GradeEvidence({
  details,
  studentRole = null,
  visibleComponents = ['quiz', 'contribution', 'completion', 'attendance'],
}: {
  details: GradeDetails
  studentRole?: string | null
  visibleComponents?: Array<GradeComponentName>
}) {
  const { tt } = useTranslationTyped()
  const { rubricTypes } = useRubricTypes()
  const copy = tt.meeting.results.grade_explainer
  const labels = tt.meeting.results.scalar_labels.chart_legends
  const { quiz, contribution, completion, attendance } = details.components
  const weights =
    details.grade.composition.status === 'available'
      ? details.grade.composition.weights
      : null
  const missedQuestions =
    quiz.evidence.status === 'available'
      ? quiz.evidence.questions.filter(
          (question) => question.isCorrect === false
        )
      : []
  const quizLosses =
    quiz.evidence.status === 'available' &&
    quiz.evidence.correctQuestionCount !== null &&
    quiz.evidence.totalQuestionCount > 0 &&
    quiz.scoreFraction !== null &&
    weights?.quiz !== null &&
    weights?.quiz !== undefined &&
    Math.abs(
      quiz.scoreFraction -
        quiz.evidence.correctQuestionCount / quiz.evidence.totalQuestionCount
    ) <= 0.006
      ? allocatePercentages({
          total: weightedPercentage({
            score: quiz.scoreFraction,
            weight: weights.quiz,
            lowered: true,
          }),
          shares: missedQuestions.map(() => 1),
        })
      : null
  const contributionLossShares = rubricLossShares({
    contribution,
    studentRole,
  })
  const evidenceScore =
    contributionLossShares === null
      ? null
      : 1 - contributionLossShares.reduce((sum, share) => sum + share, 0)
  const contributionLosses =
    evidenceScore !== null &&
    contribution.scoreFraction !== null &&
    weights?.contribution !== null &&
    weights?.contribution !== undefined &&
    Math.abs(evidenceScore - contribution.scoreFraction) <= 0.006
      ? allocatePercentages({
          total: weightedPercentage({
            score: contribution.scoreFraction,
            weight: weights.contribution,
            lowered: true,
          }),
          shares: contributionLossShares ?? [],
        })
      : null

  return (
    <div
      className="flex flex-col gap-y-4"
      data-testid="grade-explainer-evidence"
    >
      <EvidenceSection visible={visibleComponents.includes('quiz')}>
        <div className="mb-2">
          <h3 className="text-xs font-bold tracking-wider text-bi-on-surface-var uppercase">
            {copy.missed_quiz_questions()}
          </h3>
        </div>
        {quiz.evidence.status === 'unavailable' ? (
          <EvidenceUnavailable />
        ) : null}
        {quiz.evidence.status === 'no_questions' ? (
          <p className="text-xs text-bi-on-surface-var">
            {copy.no_questions()}
          </p>
        ) : null}
        {quiz.evidence.status === 'available' ? (
          missedQuestions.length ? (
            <div className="overflow-hidden rounded-[14px] border border-bi-outline-light bg-white">
              {missedQuestions.map((question, index) => (
                <details
                  key={question.questionId}
                  data-analytics-disclosure="missed_question"
                  data-analytics-item-id={question.questionId}
                  className="group/question border-b border-bi-outline-light last:border-b-0"
                >
                  <summary className="flex cursor-pointer list-none items-center gap-2.5 px-4 py-3 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-solid [&::-webkit-details-marker]:hidden">
                    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-bi-error/10 text-bi-error">
                      <X size={12} strokeWidth={3} aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1 text-[12.5px] font-semibold wrap-break-word">
                      {question.question}
                    </span>
                    {weights?.quiz === null ? (
                      <NeutralImpact />
                    ) : (
                      <LossImpact percentage={quizLosses?.[index] ?? null} />
                    )}
                    <ChevronDown
                      size={15}
                      aria-hidden="true"
                      className="shrink-0 -rotate-90 text-bi-on-surface-var transition-transform group-open/question:rotate-0"
                    />
                  </summary>
                  <div className="flex flex-col gap-y-2 border-t border-bi-outline-light px-4 py-3 text-xs">
                    <p className="flex items-start gap-2">
                      <X
                        size={14}
                        strokeWidth={2.5}
                        aria-hidden="true"
                        className="mt-0.5 shrink-0 text-bi-error"
                      />
                      <span>
                        <strong>
                          {question.studentAnswers.filter(Boolean).join(', ') ||
                            copy.no_answer()}
                        </strong>{' '}
                        <span className="text-bi-on-surface-var">
                          {copy.your_answer_label()}
                        </span>
                      </span>
                    </p>
                    {question.correctAnswer === null ? (
                      <p className="text-bi-on-surface-var">
                        {copy.correct_answer({ answer: copy.unavailable() })}
                      </p>
                    ) : (
                      <p className="flex items-start gap-2">
                        <Check
                          size={14}
                          strokeWidth={2.5}
                          aria-hidden="true"
                          className="mt-0.5 shrink-0 text-bi-deep-green"
                        />
                        <span>
                          <strong>{question.correctAnswer}</strong>{' '}
                          <span className="text-bi-on-surface-var">
                            {copy.correct_answer_label()}
                          </span>
                        </span>
                      </p>
                    )}
                    {question.explanation ? (
                      <p className="pl-[22px] leading-relaxed text-bi-on-surface-var">
                        <strong className="text-black">
                          {copy.explanation_label()}
                        </strong>{' '}
                        {question.explanation}
                      </p>
                    ) : null}
                  </div>
                </details>
              ))}
            </div>
          ) : (
            <p className="text-xs text-bi-on-surface-var">
              {copy.no_incorrect_quiz_questions()}
            </p>
          )
        ) : null}
      </EvidenceSection>

      <EvidenceSection visible={visibleComponents.includes('contribution')}>
        <div className="mb-2">
          <h3 className="text-xs font-bold tracking-wider text-bi-on-surface-var uppercase">
            {labels.totalRubricScore()}
          </h3>
        </div>
        {contribution.evidence.status === 'unavailable' ? (
          <EvidenceUnavailable />
        ) : contribution.evidence.rubrics.every(
            (_, index) =>
              weights?.contribution === 0 ||
              contributionLossShares?.[index] === 0
          ) ? (
          <p className="text-xs text-bi-on-surface-var">
            {copy.no_contribution_loss()}
          </p>
        ) : (
          <div className="overflow-hidden rounded-[14px] border border-bi-outline-light bg-white">
            {contribution.evidence.rubrics.map((rubric, index) => {
              if (
                weights?.contribution === 0 ||
                contributionLossShares?.[index] === 0
              )
                return null
              const rubricType = rubricTypes.find(
                (type) => type.type === rubric.rubricType
              )
              const scoreLabel = rubricType?.scoreDescriptions.find(
                (description) => description.score === rubric.score
              )?.title
              return (
                <details
                  key={rubric.rubricId}
                  data-analytics-disclosure="rubric"
                  data-analytics-item-id={rubric.rubricId}
                  className="group/rubric border-b border-bi-outline-light last:border-b-0"
                >
                  <summary className="flex cursor-pointer list-none items-center gap-2.5 px-4 py-3 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-solid [&::-webkit-details-marker]:hidden">
                    <span className="min-w-0 flex-1 text-[12.5px] font-semibold">
                      {rubric.name ?? copy.contribution_rubric()}
                    </span>
                    {scoreLabel ? (
                      <span
                        className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold text-black"
                        style={{
                          backgroundColor: rubricType?.getScoreBgColor(
                            rubric.score,
                            { opacity: 0.3 }
                          ),
                        }}
                      >
                        {scoreLabel}
                      </span>
                    ) : null}
                    {weights?.contribution === null ? (
                      <NeutralImpact />
                    ) : (
                      <LossImpact
                        percentage={contributionLosses?.[index] ?? null}
                      />
                    )}
                    <ChevronDown
                      size={15}
                      aria-hidden="true"
                      className="shrink-0 -rotate-90 text-bi-on-surface-var transition-transform group-open/rubric:rotate-0"
                    />
                  </summary>
                  <div className="flex flex-col gap-y-2 border-t border-bi-outline-light px-4 py-3 text-xs leading-relaxed">
                    {rubric.description ? <p>{rubric.description}</p> : null}
                    {rubric.scoreCriteria[rubric.score] ? (
                      <p>{rubric.scoreCriteria[rubric.score]}</p>
                    ) : null}
                    {rubric.justification ? (
                      <p>{rubric.justification}</p>
                    ) : null}
                    {rubric.arguments.length ? (
                      <ul className="flex list-disc flex-col gap-y-1 pl-5">
                        {rubric.arguments.map((argument, index) => (
                          <li key={`${rubric.rubricId}-${index}`}>
                            {argument}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </details>
              )
            })}
          </div>
        )}
      </EvidenceSection>

      <EvidenceSection visible={visibleComponents.includes('completion')}>
        <div className="mb-2">
          <h3 className="text-xs font-bold tracking-wider text-bi-on-surface-var uppercase">
            {labels.participationScore()}
            {completion.evidence.status === 'available'
              ? ` (${copy.measured_by({
                  metric:
                    completion.evidence.metric === 'camera_on_time'
                      ? copy.camera_on()
                      : copy.time_in_session(),
                })})`
              : null}
          </h3>
        </div>
        <div className="rounded-[14px] border border-bi-outline-light bg-white px-4 py-3">
          {completion.evidence.status === 'unavailable' ? (
            <EvidenceUnavailable />
          ) : (
            <dl className="flex flex-col gap-y-2 text-[12.5px]">
              <div className="flex items-center justify-between gap-4">
                <dt>
                  {completion.evidence.metric === 'camera_on_time'
                    ? copy.camera_on()
                    : copy.time_in_session()}
                </dt>
                <dd>
                  <WeightedImpact
                    score={completion.scoreFraction}
                    weight={weights?.completion ?? null}
                    lowered
                  />
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt>{copy.your_time()}</dt>
                <dd className="font-semibold">
                  {duration(
                    completion.evidence.metric === 'camera_on_time'
                      ? completion.evidence.cameraOnSeconds
                      : completion.evidence.durationPresentSeconds
                  )}
                </dd>
              </div>
              <div className="flex justify-between gap-4 text-bi-on-surface-var">
                <dt>{copy.group_median_time()}</dt>
                <dd>
                  {duration(completion.evidence.groupMedianDurationSeconds)}
                </dd>
              </div>
            </dl>
          )}
        </div>
      </EvidenceSection>

      <EvidenceSection visible={visibleComponents.includes('attendance')}>
        <div className="mb-2">
          <h3 className="text-xs font-bold tracking-wider text-bi-on-surface-var uppercase">
            {labels.attendanceScore()}
          </h3>
        </div>
        <div className="rounded-[14px] border border-bi-outline-light bg-white px-4 py-3 text-[12.5px]">
          {attendance.evidence.status === 'unavailable' ? (
            <EvidenceUnavailable />
          ) : (
            <div className="flex items-center justify-between gap-4">
              <p>
                {copy.attendance_summary({
                  status: attendance.evidence.attended
                    ? copy.attended()
                    : copy.no_attendance(),
                  duration: copy.duration_in_session({
                    duration: duration(
                      attendance.evidence.durationPresentSeconds
                    ),
                  }),
                })}
              </p>
              <WeightedImpact
                score={attendance.scoreFraction}
                weight={weights?.attendance ?? null}
                lowered
              />
            </div>
          )}
        </div>
      </EvidenceSection>
    </div>
  )
}

function weightedPercentage({
  score,
  weight,
  lowered,
}: {
  score: number
  weight: number
  lowered: boolean
}): number {
  return Math.round((lowered ? 1 - score : score) * weight * 10000) / 100
}

function hasWeightedImpact(row: ComponentBreakdownRow, lowered: boolean) {
  if (row.weight === null || row.weight <= 0) return false
  if (row.score === null) return true
  return lowered ? row.score < 1 : row.score > 0
}

function totalWeightedPercentage({
  rows,
  lowered,
}: {
  rows: Array<ComponentBreakdownRow>
  lowered: boolean
}): number {
  return Number(
    rows
      .reduce(
        (total, row) =>
          total +
          weightedPercentage({
            score: row.score ?? 0,
            weight: row.weight ?? 0,
            lowered,
          }),
        0
      )
      .toFixed(2)
  )
}

function BreakdownDisclosure({
  title,
  percentage,
  rows,
  details,
  components,
  lowered,
  studentRole,
}: {
  title: string
  percentage: number | null
  rows: Array<ComponentBreakdownRow>
  details: GradeDetails
  components: Array<ScoreComponent> | null
  lowered: boolean
  studentRole: string | null
}) {
  const { tt } = useTranslationTyped()
  const copy = tt.meeting.results.grade_explainer

  return (
    <details
      className="group"
      open={lowered}
      data-analytics-disclosure={lowered ? 'lowered_score' : 'earned_score'}
    >
      <summary className="flex cursor-pointer list-none items-center gap-3 rounded-[10px] bg-bi-surface px-4 py-3 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-solid [&::-webkit-details-marker]:hidden">
        <ChevronDown
          size={16}
          aria-hidden="true"
          className="shrink-0 -rotate-90 text-bi-on-surface-var transition-transform group-open:rotate-0"
        />
        <h2 className="flex-1 text-xs font-bold tracking-wider text-bi-on-surface-var uppercase">
          {title}
        </h2>
        {lowered ? (
          <LossImpact percentage={percentage} size="regular" />
        ) : (
          <span className="shrink-0 text-sm font-bold text-bi-deep-green tabular-nums">
            {percentage === null
              ? '+—%'
              : copy.earned_percentage_total({ percentage })}
          </span>
        )}
      </summary>
      <div className="mt-3 ml-4 border-l-2 border-bi-outline-light pl-4">
        {lowered ? (
          rows.length ? (
            <GradeEvidence
              details={details}
              studentRole={studentRole}
              visibleComponents={rows.map((row) => row.key)}
            />
          ) : (
            <p className="text-xs text-bi-on-surface-var">
              {percentage === null
                ? copy.evidence_unavailable()
                : copy.no_lowered_components()}
            </p>
          )
        ) : rows.length ? (
          <div
            className="overflow-hidden rounded-[14px] border border-bi-outline-light bg-white"
            data-testid="earned-components"
          >
            {rows.map(({ key, label, componentKey, score, weight }) => {
              const tooltip = components?.find(
                (component) => component.key === componentKey
              )
              return (
                <div
                  key={key}
                  className="flex items-center gap-2.5 border-b border-bi-outline-light px-4 py-3 last:border-b-0"
                >
                  <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-bi-deep-green/10 text-bi-deep-green">
                    <Check size={12} strokeWidth={3} aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <strong className="flex items-center gap-1 text-[12.5px]">
                      {label}
                      {tooltip ? (
                        <BreakoutTooltip content={tooltip.tooltip}>
                          <button
                            type="button"
                            aria-label={copy.component_info({
                              component: label,
                            })}
                            data-testid={`final-score-breakdown-tooltip-${componentKey}`}
                            className="inline-flex min-h-6 min-w-6 items-center justify-center rounded-sm focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-solid"
                          >
                            <InfoIcon size={14} />
                          </button>
                        </BreakoutTooltip>
                      ) : null}
                    </strong>
                    <span className="block text-[11px] text-bi-on-surface-var">
                      {copy.score_and_weight({
                        score: percent(score),
                        weight: percent(weight),
                      })}
                    </span>
                  </span>
                  <WeightedImpact
                    score={score}
                    weight={weight}
                    lowered={false}
                  />
                </div>
              )
            })}
          </div>
        ) : (
          <p className="text-xs text-bi-on-surface-var">
            {percentage === null
              ? copy.evidence_unavailable()
              : copy.no_earned_components()}
          </p>
        )}
      </div>
    </details>
  )
}

export function ComponentBreakdown({
  details,
  components,
  studentRole = null,
}: {
  details: GradeDetails
  components: Array<ScoreComponent> | null
  studentRole?: string | null
}) {
  const { tt } = useTranslationTyped()
  const copy = tt.meeting.results.grade_explainer
  const labels = tt.meeting.results.scalar_labels.chart_legends
  const rowDefinitions: Array<{
    key: GradeComponentName
    label: string
    componentKey: ScoreComponentKey
  }> = [
    { key: 'quiz', label: labels.quizScore(), componentKey: 'quizScore' },
    {
      key: 'contribution',
      label: labels.totalRubricScore(),
      componentKey: 'totalRubricScore',
    },
    {
      key: 'completion',
      label: labels.participationScore(),
      componentKey: 'participationScore',
    },
    {
      key: 'attendance',
      label: labels.attendanceScore(),
      componentKey: 'attendanceScore',
    },
  ]
  const weights =
    details.grade.composition.status === 'available'
      ? details.grade.composition.weights
      : null
  const rows: Array<ComponentBreakdownRow> = rowDefinitions.map((row) => ({
    ...row,
    score: details.components[row.key].scoreFraction,
    weight: weights?.[row.key] ?? null,
  }))
  const percentagesAvailable =
    weights !== null &&
    rows.every((row) => (row.weight ?? 0) === 0 || row.score !== null)
  const earnedPercentage = percentagesAvailable
    ? totalWeightedPercentage({ rows, lowered: false })
    : null
  const loweredPercentage = percentagesAvailable
    ? totalWeightedPercentage({ rows, lowered: true })
    : null

  return (
    <section className="flex flex-col gap-y-3">
      <BreakdownDisclosure
        title={copy.what_lowered_score()}
        percentage={loweredPercentage}
        rows={rows.filter((row) => hasWeightedImpact(row, true))}
        details={details}
        components={components}
        lowered
        studentRole={studentRole}
      />
      <BreakdownDisclosure
        title={copy.what_you_earned()}
        percentage={earnedPercentage}
        rows={rows.filter((row) => hasWeightedImpact(row, false))}
        details={details}
        components={components}
        lowered={false}
        studentRole={studentRole}
      />
    </section>
  )
}

function AssignmentDate({ label, value }: { label: string; value: Date }) {
  const formattedDate = DateTime.fromJSDate(value)

  return (
    <div className="min-w-0 flex-1 px-4 py-3">
      <dt className="text-[10px] font-bold tracking-wider text-bi-on-surface-disabled uppercase">
        {label}
      </dt>
      <dd className="mt-1 text-sm font-bold text-black">
        <time dateTime={value.toISOString()}>
          <span className="block">
            {formattedDate.toFormat('ccc, LLL d, yyyy')}
          </span>
          <span className="mt-0.5 block text-xs font-medium text-bi-on-surface-disabled">
            {formattedDate.toFormat('h:mm a')}
          </span>
        </time>
      </dd>
    </div>
  )
}

const DESCRIPTION_PREVIEW_WORDS = 80

function AssignmentDescription({ description }: { description: string }) {
  const { tt } = useTranslationTyped()
  const descriptionId = useId()
  const [expanded, setExpanded] = useState(false)
  const words = description.trim().match(/\S+/g) ?? []
  const isLong = words.length > DESCRIPTION_PREVIEW_WORDS
  const visibleDescription =
    isLong && !expanded
      ? `${words.slice(0, DESCRIPTION_PREVIEW_WORDS).join(' ')}…`
      : description

  return (
    <div className="min-w-0 text-[12.5px] leading-relaxed font-medium text-bi-charcoal [&_.slide-deck-description-markdown_p:last-child]:mb-0">
      <div id={descriptionId}>
        <SlideDeckDescriptionMarkdown>
          {visibleDescription}
        </SlideDeckDescriptionMarkdown>
      </div>
      {isLong ? (
        <button
          type="button"
          aria-controls={descriptionId}
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          className="mt-2 inline-flex min-h-6 cursor-pointer items-center font-semibold text-bi-deep-teal underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-solid"
        >
          {expanded ? tt.meeting.view_less() : tt.meeting.view_more()}
        </button>
      ) : null}
    </div>
  )
}

export function AssignmentContext({
  assignmentName,
  sectionLabel,
  thumbnailUrl,
  heroImageUrl,
  description,
  availableAt,
  dueAt,
}: {
  assignmentName: string
  sectionLabel: string
  thumbnailUrl?: string
  heroImageUrl?: string | null
  description?: string
  availableAt?: Date
  dueAt?: Date
}) {
  const { tt } = useTranslationTyped()
  const copy = tt.meeting.results.grade_explainer

  return (
    <details
      data-testid="grade-explainer-assignment-context"
      data-analytics-disclosure="assignment_context"
      className="group overflow-hidden rounded-[14px] border border-bi-outline-light"
    >
      <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-solid [&::-webkit-details-marker]:hidden">
        <ChevronDown
          size={16}
          aria-hidden="true"
          className="shrink-0 -rotate-90 text-bi-on-surface-var transition-transform group-open:rotate-0"
        />
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt=""
            className="h-10 w-10 shrink-0 object-contain"
          />
        ) : (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-bi-light-teal text-bi-deep-teal">
            <BookOpen size={20} aria-hidden="true" />
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-bold wrap-break-word text-black">
            {assignmentName}
          </span>
          {sectionLabel ? (
            <span className="mt-0.5 block text-xs wrap-break-word text-bi-on-surface-disabled">
              {sectionLabel}
            </span>
          ) : null}
        </span>
      </summary>
      <div className="flex flex-col gap-y-4 border-t border-bi-outline-light px-4 py-4 sm:px-5">
        {availableAt || dueAt ? (
          <dl className="flex flex-col divide-y divide-bi-outline-light rounded-xl border border-bi-outline-light sm:flex-row sm:divide-x sm:divide-y-0">
            {availableAt ? (
              <AssignmentDate label={copy.available()} value={availableAt} />
            ) : null}
            {dueAt ? <AssignmentDate label={copy.due()} value={dueAt} /> : null}
          </dl>
        ) : null}
        {heroImageUrl || description ? (
          <div className="flex flex-col items-start gap-4 sm:flex-row">
            {heroImageUrl ? (
              <div className="aspect-video w-full shrink-0 sm:w-56">
                <GoogleStorageImage
                  src={heroImageUrl}
                  alt={copy.deck_image_alt({ assignment: assignmentName })}
                  namespace="slideDeckMaterial"
                  loader={
                    <div className="h-full w-full rounded-[10px] bg-bi-surface motion-safe:animate-pulse" />
                  }
                  className="h-full w-full rounded-[10px] object-contain"
                />
              </div>
            ) : null}
            {description ? (
              <AssignmentDescription description={description} />
            ) : null}
          </div>
        ) : null}
      </div>
    </details>
  )
}

export function ExplanationSkeleton() {
  const { tt } = useTranslationTyped()
  const copy = tt.meeting.results.grade_explainer

  return (
    <div
      role="status"
      aria-label={copy.loading_explanation()}
      className="flex flex-col gap-y-5"
    >
      <section className="flex gap-3 rounded-[14px] bg-bi-warning-soft px-4 py-3.5">
        <SparklesIcon size={19} color="#ffca00" className="mt-0.5 shrink-0" />
        <div className="w-full pt-1">
          <div
            aria-hidden="true"
            className="flex flex-col gap-y-2 motion-safe:animate-pulse"
          >
            <div className="h-3 w-full rounded-sm bg-bi-outline-light" />
            <div className="h-3 w-4/5 rounded-sm bg-bi-outline-light" />
          </div>
        </div>
      </section>
      {[
        { title: copy.what_went_well(), color: 'text-bi-deep-green' },
        { title: copy.what_affected_score(), color: 'text-bi-error' },
        { title: copy.how_to_improve(), color: 'text-bi-dark-teal' },
      ].map(({ title, color }) => (
        <section key={title}>
          <h2
            className={`mb-2 text-xs font-bold tracking-wider uppercase ${color}`}
          >
            {title}
          </h2>
          <div
            aria-hidden="true"
            className="flex flex-col gap-y-2 motion-safe:animate-pulse"
          >
            <div className="h-3 w-full rounded-sm bg-bi-outline-light" />
            <div className="h-3 w-11/12 rounded-sm bg-bi-outline-light" />
            <div className="h-3 w-3/5 rounded-sm bg-bi-outline-light" />
          </div>
        </section>
      ))}
    </div>
  )
}

export function GradeExplanationContent({
  explanation,
}: {
  explanation: {
    executiveSummary: string | null
    positive: string | null
    negative: string | null
    suggestion: string | null
  } | null
}) {
  const { tt } = useTranslationTyped()
  const copy = tt.meeting.results.grade_explainer

  return (
    <>
      <section className="flex gap-3 rounded-[14px] bg-bi-warning-soft px-4 py-3.5">
        <SparklesIcon size={19} color="#ffca00" className="mt-0.5 shrink-0" />
        <div>
          <p className="text-[13px] leading-relaxed font-medium text-bi-charcoal">
            {explanation?.executiveSummary ?? copy.explanation_unavailable()}
          </p>
        </div>
      </section>
      <section>
        <h2 className="mb-2 text-xs font-bold tracking-wider text-bi-deep-green uppercase">
          {copy.what_went_well()}
        </h2>
        <p className="text-[13.5px] leading-relaxed font-medium text-bi-charcoal">
          {explanation?.positive ?? copy.explanation_section_unavailable()}
        </p>
      </section>
      <section>
        <h2 className="mb-2 text-xs font-bold tracking-wider text-bi-error uppercase">
          {copy.what_affected_score()}
        </h2>
        <p className="text-[13.5px] leading-relaxed font-medium text-bi-charcoal">
          {explanation?.negative ?? copy.explanation_section_unavailable()}
        </p>
      </section>
      <section>
        <h2 className="mb-2 text-xs font-bold tracking-wider text-bi-dark-teal uppercase">
          {copy.how_to_improve()}
        </h2>
        <p className="text-[13.5px] leading-relaxed font-medium text-bi-charcoal">
          {explanation?.suggestion ?? copy.explanation_section_unavailable()}
        </p>
      </section>
    </>
  )
}

export const GradeExplainerView = observer(function GradeExplainerView({
  cubit,
  backToAssignments = false,
  onBack,
}: {
  cubit: ReturnType<typeof useMeetingResultsCubit>
  backToAssignments?: boolean
  onBack: () => void
}) {
  const { tt } = useTranslationTyped()
  const copy = tt.meeting.results.grade_explainer
  const data = useGradeExplainer({ cubit })
  const appeal = useGradeAppeal(cubit)
  const { studentGradeAppealsEnabled } = useFeatureFlags()
  const isOwnGrade = cubit.userId === useCurrentUser().uid
  const [editingAppeal, setEditingAppeal] = useState(false)
  const appealButtonRef = useRef<HTMLButtonElement>(null)
  const restoreAppealButtonFocus = useRef(false)

  useEffect(() => {
    if (!editingAppeal && restoreAppealButtonFocus.current) {
      appealButtonRef.current?.focus()
      restoreAppealButtonFocus.current = false
    }
  }, [editingAppeal])
  const showExplanation =
    !data.explanationPending &&
    Boolean(data.explanation || data.explanationUnavailable)
  const components = useScoreComponents(cubit)
  const computedScore = data.details?.grade.totalScoreFraction
  const resolvedAppealOverride =
    appeal.appeal?.status === 'accepted' ||
    appeal.appeal?.status === 'partially-accepted'
  const displayedScore =
    appeal.overrideFinalScore ??
    (resolvedAppealOverride ||
    computedScore === null ||
    computedScore === undefined
      ? null
      : toFinalScorePercent(computedScore))
  const showAppeal =
    isOwnGrade &&
    studentGradeAppealsEnabled &&
    components &&
    (appeal.appeal ||
      (cubit.assignment.data.gradeAppealsEnabled === true &&
        (appeal.canShowAction || appeal.isError || editingAppeal)))
  const statusPanel =
    appeal.appeal && components ? (
      <GradeAppealStatusPanel
        appeal={appeal.appeal}
        components={components}
        overrideFinalPercent={appeal.overrideFinalScore}
      />
    ) : null
  const appealDeadlineText = appeal.appealWindowClosesAt
    ? DateTime.fromISO(appeal.appealWindowClosesAt).toLocaleString(
        DateTime.DATETIME_MED
      )
    : null
  const slideDeck = cubit.meetingCubit.slideDeck.data
  const assignmentName = slideDeck.slideDeckName
  const sectionLabel = cubit.meetingCubit.section.data.className
  const groupName = cubit.meetingCubit.roomState.roomStateName
  const availableAt = cubit.assignment.getEffectiveAssignedAt(cubit.userId)
  const dueAt = cubit.assignment.getEffectiveDueDate(cubit.userId)
  const student = cubit.meetingCubit.users.find(
    (user) => user.id === cubit.userId
  )
  const studentInitials =
    [student?.data.firstName?.[0], student?.data.lastName?.[0]]
      .filter(Boolean)
      .join('')
      .toUpperCase() ||
    data.details?.student.firstName?.slice(0, 2).toUpperCase() ||
    '•'
  const studentRole =
    cubit.meetingCubit.roomState.data.assignmentRoles?.[cubit.userId] ?? null
  return (
    <div
      data-testid="grade-explainer-view"
      className="mx-auto w-full max-w-[820px] pb-10"
    >
      <div className="mb-4">
        <BreakoutButton
          kind="tertiary"
          icon={<ArrowLeft size={16} aria-hidden="true" />}
          onClick={onBack}
        >
          {backToAssignments
            ? copy.back_to_assignments()
            : copy.back_to_assignment()}
        </BreakoutButton>
      </div>
      <article
        className="rounded-[24px] bg-white px-5 py-7 shadow-[0_16px_48px_rgba(0,0,0,0.05)] sm:px-9 sm:py-8"
        onClickCapture={(event) => {
          if (!isOwnGrade || !(event.target instanceof Element)) return
          const disclosure = event.target.closest('summary')?.parentElement
          if (!(disclosure instanceof HTMLDetailsElement)) return
          const section = disclosure.dataset.analyticsDisclosure
          if (!section) return
          cubit.logEvent('grade_explainer_disclosure_clicked', {
            section,
            item_id: disclosure.dataset.analyticsItemId,
            open: !disclosure.open,
          })
        }}
      >
        <header className="mb-6 flex flex-wrap items-center gap-3.5">
          <span
            aria-hidden="true"
            className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full bg-bi-accent text-base font-bold text-black"
          >
            {studentInitials}
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="text-[26px] leading-tight font-bold tracking-tight text-black">
              {copy.heading()}
            </h1>
            <p className="mt-0.5 text-xs font-medium wrap-break-word text-bi-on-surface-disabled">
              {copy.assignment_group({
                assignment: assignmentName,
                group: cubit.assignment.isSolo ? copy.solo_room() : groupName,
              })}
            </p>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-1 text-right">
            <p
              data-testid="grade-explainer-computed-score"
              aria-live="polite"
              aria-atomic="true"
              className="text-[34px] leading-none font-bold tracking-tight text-black tabular-nums"
            >
              <span className="sr-only">
                {appeal.appealOverride
                  ? tt.student_library.redesign.final_grade()
                  : copy.computed_grade()}
                :{' '}
              </span>
              {displayedScore === null ? '—' : `${displayedScore}%`}
              {!appeal.appealOverride && displayedScore !== null ? (
                <BreakoutTooltip content={copy.grade_rounding_note()}>
                  <button
                    type="button"
                    aria-label={copy.grade_rounding_note()}
                    className="ml-0.5 cursor-help align-super text-sm leading-none underline decoration-dotted underline-offset-0 focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-solid"
                  >
                    *
                  </button>
                </BreakoutTooltip>
              ) : null}
            </p>
            {appeal.appealOverride && displayedScore !== null ? (
              <AppealOverrideIndicator />
            ) : null}
          </div>
        </header>

        <div className="flex flex-col gap-y-5">
          {showAppeal && resolvedAppealOverride ? (
            <section
              className="border-b border-bi-outline-light pb-5"
              data-testid="grade-explainer-appeal"
            >
              {statusPanel}
            </section>
          ) : null}
          {data.detailsPending ? (
            <p role="status">{copy.loading_details()}</p>
          ) : null}
          {data.detailsError || (!data.detailsPending && !data.details) ? (
            <div role="alert" className="rounded-xl bg-bi-surface p-4">
              <p>{copy.details_failed()}</p>
              <BreakoutButton kind="secondary" onClick={data.retry}>
                {copy.retry()}
              </BreakoutButton>
            </div>
          ) : null}

          {data.explanationPending ? <ExplanationSkeleton /> : null}
          {data.explanationError ? (
            <div role="alert" className="rounded-xl bg-bi-surface p-4">
              <p>{copy.explanation_failed()}</p>
              <BreakoutButton kind="secondary" onClick={data.retry}>
                {copy.retry()}
              </BreakoutButton>
            </div>
          ) : null}
          {showExplanation ? (
            <GradeExplanationContent explanation={data.explanation} />
          ) : null}

          {data.details ? (
            <>
              <div className="border-t border-bi-outline-light pt-4">
                <AssignmentContext
                  assignmentName={assignmentName}
                  sectionLabel={sectionLabel}
                  thumbnailUrl={slideDeck.slideDeckImageURL}
                  heroImageUrl={data.heroImageUrl}
                  description={slideDeck.slideDeckDescription}
                  availableAt={availableAt}
                  dueAt={dueAt}
                />
              </div>
              <ComponentBreakdown
                details={data.details}
                components={components}
                studentRole={studentRole}
              />
            </>
          ) : null}
          {showAppeal && !resolvedAppealOverride ? (
            <section
              className="border-t border-bi-outline-light pt-6"
              data-testid="grade-explainer-appeal"
            >
              {appeal.appeal ? (
                statusPanel
              ) : (
                <>
                  {!editingAppeal && !appeal.isError && (
                    <h2 className="mb-1 text-base font-bold text-black">
                      {tt.meeting.results.appeal.filing_prompt()}
                    </h2>
                  )}
                  {appealDeadlineText && (
                    <p className="mb-4 text-xs font-medium text-bi-on-surface-disabled">
                      {appeal.actionBlockedReason === 'window_closed'
                        ? tt.meeting.results.appeal.deadline_closed({
                            deadline: appealDeadlineText,
                          })
                        : tt.meeting.results.appeal.deadline({
                            deadline: appealDeadlineText,
                          })}
                    </p>
                  )}
                  {appeal.isError && (
                    <div role="alert" className="rounded-xl bg-bi-surface p-4">
                      <p>{tt.meeting.results.appeal.load_failed()}</p>
                      <BreakoutButton
                        kind="secondary"
                        onClick={() => void appeal.refetch()}
                      >
                        {copy.retry()}
                      </BreakoutButton>
                    </div>
                  )}
                  {editingAppeal && appeal.actionBlockedReason && (
                    <p role="alert" className="mb-4 text-xs text-bi-charcoal">
                      {appeal.actionBlockedReason === 'not_graded'
                        ? tt.meeting.results.appeal.not_graded_tooltip()
                        : tt.meeting.results.appeal.window_closed_tooltip()}
                    </p>
                  )}
                  {editingAppeal ? (
                    <GradeAppealForm
                      cubit={cubit}
                      canSubmit={appeal.canSubmitDraft}
                      onClose={() => {
                        restoreAppealButtonFocus.current = true
                        setEditingAppeal(false)
                      }}
                    />
                  ) : !appeal.isError ? (
                    <GradeAppealActionButton
                      isPending={appeal.isPending}
                      canShow={appeal.canShowAction}
                      canFile={appeal.canFile}
                      blockedReason={appeal.actionBlockedReason}
                      onClick={() => {
                        cubit.logEvent('grade_appeal_button_clicked')
                        setEditingAppeal(true)
                      }}
                      buttonRef={appealButtonRef}
                    />
                  ) : null}
                </>
              )}
            </section>
          ) : null}
        </div>
      </article>
    </div>
  )
})
